import { type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

import { LiveRegion } from '@/components/feedback/LiveRegion';
import { OfflineBanner } from '@/components/feedback/OfflineBanner';
import { Toaster } from '@/components/feedback/Toaster';
// Language switcher temporarily hidden from the UI (ADR-038). Do not delete.
// import { LanguageSwitcher } from '@/components/layout/LanguageSwitcher';
import { SafeArea } from '@/components/layout/SafeArea';
import { SkipLink } from '@/components/layout/SkipLink';
import { env } from '@/config/env';
// import { type Locale, LOCALE_LABELS, SUPPORTED_LOCALES } from '@/constants/locales';
import { isSafeAssetUrl } from '@/helpers/assetUrl';
// import { buildLocaleUrl } from '@/i18n/detectLocale';
// import { currentLocale } from '@/i18n/i18n';
// import { loadAndChangeLocale } from '@/i18n/loadLocale';
import { useSessionState } from '@/state';

import { CheckoutFrame } from './CheckoutFrame';
import { PaymentGuide } from './PaymentGuide';
import { SessionTimers } from './SessionTimers';

// const LANGUAGE_OPTIONS = SUPPORTED_LOCALES.map((code) => ({
//   code,
//   label: LOCALE_LABELS[code],
// }));

export interface CashierLayoutProps {
  children: ReactNode;
}

/**
 * The branded shell. Owns the skip link, the single live-region pair, the offline banner, and the
 * toaster so screens never mount a second copy of any of them.
 */
export function CashierLayout({ children }: CashierLayoutProps) {
  const { t } = useTranslation('common');
  const { t: tCashier } = useTranslation('cashier');
  const state = useSessionState();
  // const active = currentLocale();
  const branding = state.branding;
  const displayName = branding?.displayName ?? env.appName;
  const scheme = state.effectiveScheme;
  const assets = branding === null ? null : scheme === 'dark' ? branding.dark : branding.light;
  const logoUrl = assets !== null && isSafeAssetUrl(assets.logoUrl) ? assets.logoUrl : null;
  const isSandbox = state.token?.startsWith('sandbox.') === true;

  // const handleLocale = (locale: Locale) => {
  //   void loadAndChangeLocale(locale).then((ok) => {
  //     if (!ok) return;
  //     window.history.replaceState(null, '', buildLocaleUrl(window.location.href, locale));
  //   });
  // };

  return (
    <div className="bg-surface-muted text-text flex min-h-dvh flex-col">
      <SkipLink label={t('a11y.skipToContent')} />
      <LiveRegion />
      <Toaster dismissLabel={t('actions.dismiss')} />
      <SessionTimers>
        <SafeArea className="flex min-h-dvh flex-1 flex-col">
          <OfflineBanner
            title={t('offline.title')}
            body={t('offline.body')}
            offlineAnnouncement={t('offline.title')}
            onlineAnnouncement={t('offline.reconnected')}
          />
          {state.banners.refreshedAfterConflict && (
            <p
              role="status"
              className="border-border bg-primary-soft text-text border-b px-4 py-2 text-center text-sm sm:px-5"
            >
              {t('conflictRefreshed')}
            </p>
          )}
          <CheckoutFrame
            mainProps={{ id: 'main-content', tabIndex: -1 }}
            banner={
              isSandbox ? (
                <p
                  role="status"
                  className="border-border bg-surface-muted text-warning border-b px-4 py-1.5 text-center text-xs font-medium sm:px-5"
                >
                  {tCashier('sandbox.banner')}
                </p>
              ) : undefined
            }
            header={
              <header className="border-border flex items-center gap-2.5 border-b px-4 py-2.5 sm:px-5">
                {logoUrl !== null ? (
                  <img
                    src={logoUrl}
                    alt={t('brand.logoAlt', { name: displayName })}
                    width={104}
                    height={28}
                    className="h-7 w-auto max-w-[6.5rem] shrink-0 object-contain"
                  />
                ) : null}
                <div className="min-w-0 flex-1">
                  <p className="text-text-muted text-xs leading-tight">{tCashier('payingTo')}</p>
                  <p className="text-text truncate text-sm leading-tight font-semibold">
                    {displayName}
                  </p>
                </div>
                <PaymentGuide />
              </header>
            }
            footer={
              <footer className="mt-auto flex flex-col gap-2 px-4 py-3 pb-[max(0.75rem,env(safe-area-inset-bottom))] sm:px-5">
                {branding?.footerText !== undefined &&
                  branding.footerText !== null &&
                  branding.footerText !== '' && (
                    <p className="text-text-muted text-center text-xs leading-relaxed">
                      {branding.footerText}
                    </p>
                  )}
                {/* <p className="text-text-muted text-center text-xs">{t('brand.poweredBy')}</p> */}
                {/* Language switcher temporarily hidden from the UI (ADR-038). Do not delete. */}
                {/*
                <LanguageSwitcher
                  locales={LANGUAGE_OPTIONS}
                  active={active}
                  onSelect={handleLocale}
                  label={t('footer.language')}
                />
                */}
              </footer>
            }
          >
            {children}
          </CheckoutFrame>
        </SafeArea>
      </SessionTimers>
    </div>
  );
}
